<template>
	<div class="cards-container">
		<div class="card">
			<div class="card-title">{{ getString('force_login') }}</div>
			<div class="card-body">
				<div class="users-container">
					<div class="col-12" v-if="!correctCountry">
						<div class="row">
							<p class="mismatch-country-phrase">{{countryMismatchPhrase}}</p>
						</div>
					</div>
					<div class="user" v-for="user in business.users">
						<div class="user-info">
							<div class="name">{{ user.email }}</div>
							<div class="permissions">
								<div class="permission" v-for="permission in getPermissionIcons(user.permissions)"
									 :title="permission.title" v-html="permission.icon"></div>
							</div>
						</div>
						<AppButton outline :text="getString('login')" @click="forceLogin(user, business?.country)" />
					</div>
				</div>
			</div>
		</div>
		<div class="card">
			<div class="card-title">{{ getString('transfers') }}</div>
			<div class="card-body">
				<div class="no-stripe" v-if="!business.stripe_account">
					<p class="mt-10">{{ getString('business_no_stripe') }}</p>
				</div>
				<div class="transfers-types mt-10 mb-10" v-else>
					<div class="row align-end space-between">
						<AppTextInput :label="getString('statement_prefix')" :placeholder="getString('statement_prefix')" :value="business.transfer_prefix ? business.transfer_prefix : ''" @changed-text="changePrefix" />
						<AppButton :loading="loadingTransferPrefix" :text="getString('save')" outline @click="savePrefix" />
					</div>
					<div class="divider-line mt-20 mb-10"></div>
					<div class="row space-between align-end pt-10 pb-10" :class="{disabled: diabledEditPayouts}">
						<AppSelect class="select-of-row" :label="getString('payout_interval_edit')" ref="business-payout-interval" :values="intervalSelectItems" :selected-index="selectedStripeIntervalIndex(business.payout_interval)" />
						<AppButton :paused="loadingPayoutDelay" outline :text="getString('save')" :loading="loadingPayoutInterval" @click="clickSavePayoutInterval" />
					</div>
					<div class="divider-line m-10"></div>
					<div class="row space-between align-end pt-10 pb-10" :class="{disabled: diabledEditPayoutsDelay}">
						<AppSelect class="select-of-row" :label="getString('payout_delay_edit')" ref="business-payout-delay" :values="delaySelectItems" :selected-index="selectedStripeDelayIndex(business.payout_delay)" />
						<AppButton :paused="loadingPayoutInterval" outline :text="getString('save')" :loading="loadingPayoutDelay" @click="clickSavePayoutDelay" />
					</div>
				</div>
			</div>
		</div>
		<div class="card">
			<div class="card-title">{{ getString('commissions_order_pay') }}</div>
			<div class="card-body">
				<div class="commissions-container">
					<AppList :items="feerulesStandard" >
						<template #feerule="{item}">
							<div class="list-item-header">
								<div @click="toggleFeeRule(item,'standard')">
									<p class="list-item-title">{{ item.title }}</p>
									<div class="list-item-fee-recap">{{ getString("commissions") }}: <span>{{ readableEurValue(item.fixed/100) }}</span> | <span>{{ item.percent }}%</span> | <span>+{{ item.premium }}%</span> | {{ readableEurValue(item.alternative_fixed/100) }} | {{ item.alternative_percent }}%</div>
								</div>
								<AppClickableIcon :autoSetActive="false" class="delete-button" icon-name="iconBin" @click="deleteFeeRule(item)" />
							</div>
						</template>
						<template #feeRulesMod="{ item }">
							<FeeRulesAppList :item="item" :type="'standard'" @changed-value="(which,val) => { changedValue(item,'standard',val,which) }" />
						</template>
					</AppList>
					<div class="add-fee-rule" @click="addNewFeeRule( 'standard' )">
						<AppClickableIcon class="icon-plus" icon-name="iconPlus" />
						<div>{{ getString('add_new_rule') }}</div>
					</div>
					<div class="row flex-end mt-15">
						<AppButton outline :text="getString('save')" @click="saveNewFeeRules( 'standard' )" />
					</div>
				</div>
			</div>
		</div>
		<div class="card">
			<div class="card-title">{{ getString('commissions_terminal') }}</div>
			<div class="card-body">
				<div v-if="editablePOSFees" class="commissions-container">
					<AppList :items="feerulesPOS" >
						<template #feerule="{item}">
							<div class="list-item-header">
								<div @click="toggleFeeRule(item,'pos')">
									<p class="list-item-title">{{ item.title }}</p>
									<div class="list-item-fee-recap">{{ getString("commissions") }}: <span>{{ readableEurValue(item.fixed/100) }}</span> | <span>{{ getEffectivePOSPercent(item.percent) }}%</span> | <span>+{{ item.premium }}%</span></div>
								</div>
								<AppClickableIcon :autoSetActive="false" class="delete-button" icon-name="iconBin" @click="deleteFeeRule(item)" />
							</div>
						</template>
						<template #feeRulesMod="{ item }">
							<FeeRulesAppList :item="item" :type="'pos'" @changed-value="(which,val) => { changedValue(item,'pos',val,which) }" />
						</template>
					</AppList>
					<div class="add-fee-rule" @click="addNewFeeRule( 'pos' )">
						<AppClickableIcon class="icon-plus" icon-name="iconPlus" />
						<div>{{ getString('add_new_rule') }}</div>
					</div>
					<div class="row flex-end mt-15">
						<AppButton :loading="loadingCommOp" outline :text="getString('save')" @click="saveNewFeeRules( 'pos' )" />
					</div>
				</div>
				<div v-else class="commissions-container">
					<p>{{ getString('commissions_terminal_plan') }}</p>
					<p class="text-align-center mt-10"><b>{{planPOSDefaultFees}}%</b></p>
				</div>
			</div>
		</div>
		<div class="card">
			<div class="card-title">{{ getString('change_business_data') }}</div>
			<div class="card-body">
				<div class="row align-end space-between pt-10 pb-10">
					<AppSelect class="select-of-row" ref="business-type" :values="[getString('food_and_beverage'), getString('events'), getString('retail')]" :selected-index="getTypeIndex(business.type)" :label="getString('business_type')" />
					<AppButton outline :text="getString('save')" :loading="loadingType" @click="changeBusinessType(business)" />
				</div>
				<div class="divider-line mb-10"></div>
				<div class="row space-between align-end pt-10 pb-10" v-if="this.superStore.isMega">
					<AppSwitch :checked="business.allow_tap_to_pay" @switched="active => business.allow_tap_to_pay = active" :label="getString('tap_to_pay_title')" />
					<AppButton outline :text="getString('save')" @click="saveTapToPayFlag" :loading="savingTapToPayFlag"/>
				</div>
			</div>
		</div>
		<div class="card">
			<div class="card-title">{{ getString('credit_debit') }}</div>
			<div class="card-body">
				<div class="row space-between pt-10 pb-10">
					<div class="current-balance" :class="{positive: business.openBalance > 0, negative: business.openBalance < 0}"><span>{{ getString('curr_balance') }}</span><br>{{ (Number(business.openBalance)/100).toFixed(2)+"€" }}</div>
					<div class="change-balance">
						<AppNumberInput ref="credit-debit" symbol="€" :min="-5000" :max="5000" :increment="1" type="number" :label="getString('new_balance')" :value="Number(business.openBalance)/100" />
						<AppNumberInput class="mt-10" ref="repayment-rate" symbol="%" :min="5" :max="100" :increment="1" type="number" :label="getString('repayment_rate')" :value="business.repayment_rate" right-side-symbol/>
						<AppButton class="mt-15" outline :text="getString('save')" :loading="loadingCredit" @click="changeBusinessCredit(business)" />
					</div>
				</div>
			</div>
		</div>
		<div class="card">
			<div class="card-title">{{ getString('report_commissions') }}</div>
			<div class="card-body">
				<div class="row space-between pt-10 pb-10">
					<AppButton outline :text="getString('report')" :loading="loadingReport" @click="reportCommissions(business.business_id)" />
				</div>
			</div>
		</div>
		<div class="card">
			<div class="card-title">{{ getString('new_invoice') }}</div>
			<div class="card-body">
				<div class="row space-between quotes-buttons">
					<AppButton outline :disabled="disabledQuote" :text="getString('create_new')" @click="newInvoiceActive = true" />
					<AppButton outline :text="getString('quotes_history')" @click="quotesHistoryActive = true" />
				</div>
			</div>
		</div>
		<AppNotification ref="success-fee-rules" :title="getString('success')" :body-text="getString('fee_rules_success')" :type="1" />
		<AppNotification ref="error-fee-rules" :title="getString('error')" :body-text="getString('error_fee_rules')" :type="-2" />
		<AppNotification ref="error-validate-fee-rules" :title="getString('error')" :body-text="getString('error_validate_fee_rules')" :type="-2" />
		<AppNotification ref="success-generic" :title="getString('success')" :body-text="getString('operation_executed')" :type="1" />
		<AppNotification ref="error-generic" :title="getString('error')" :body-text="getString('error_generic')" :type="-2" />
		<AppNotification ref="percentage-fee-low" :title="getString('insufficient_permissions')" :body-text="getString('low_fee_need_ultra_user', [superStore.minFeePerAllowed])" :type="-2" :duration="7000" />
		<AppNotification ref="ultra-not" :title="getString('insufficient_permissions')" :body-text="getString('need_ultra_user')" :type="-2" :duration="7000" />
		<AppNotification ref="plan-not" :title="getString('error')" :body-text="getString('plan_pos_fees_not_updatable')" :type="-2" :duration="7000" />
		<AppNotification ref="ultra-not-comm" :title="getString('insufficient_permissions')" :body-text="getString('need_ultra_user_comm')" :type="-2" :duration="7000" />
		<AppBottomSheet :forceWidth="bottomSheetWidth" :active="newInvoiceActive" @toggling-popup="closeBottomSheetInvoice">
			<div class="new-invoice-container" v-if="newInvoiceActive">
				<NewInvoice :business="business" :editing-quote="editingQuote" @close="closeNewInvoice" />
			</div>
		</AppBottomSheet>
		<AppBottomSheet :forceWidth="bottomSheetWidthSmall" :active="transactionsReportData !== null" @toggling-popup="closeBottomSheet">
            <div class="transactions-report-content" v-if="transactionsReportData !== null">
                <ModalHeader :title="getString('report_commissions')" />
                <div class="report-container">
                    <div class="date-range">
                        <DatePicker ref="date-picker" v-model.range="selectedRange" @update:modelValue="changedCalendarDates" color="purple" :is-dark="userStore.darkMode">
                            <template #default="{ togglePopover }">
                                <div ref="period-phrases" class="period-phrases" @click="togglePopover">
                                    <p class="period">{{periodPhrase}}</p>
                                </div>
                            </template>
                        </DatePicker>
                    </div>
                    <div class="report-row">
                        <div class="report-title">
                            <div class="report-title-main">{{ getString('qromo_net') }}</div>
                            <div class="report-title-secondary">{{ getString('orders') }}</div>
                            <div class="report-title-secondary">{{ getString('pos') }}</div>
                        </div>
                        <div class="report-value">
                            <div class="report-value-main">{{ transactionsReportData ? readableEurValue((transactionsReportData.fee/100)-transactionsReportData.network_costs-transactionsReportData.stripe_costs) : '-' }}</div>
                            <div class="report-value-secondary">{{ transactionsReportData ? readableEurValue((transactionsReportData.order_fee/100)-transactionsReportData.order_network_costs-transactionsReportData.order_stripe_costs) : '-' }}</div>
                            <div class="report-value-secondary">{{ transactionsReportData ? readableEurValue((transactionsReportData.pos_fee/100)-transactionsReportData.pos_network_costs-transactionsReportData.pos_stripe_costs) : '-' }}</div>
                        </div>
                    </div>
                    <div class="report-row">
                        <div class="report-title">
                            <div class="report-title-main">{{ getString('qromo_fee') }}</div>
                            <div class="report-title-secondary">{{ getString('orders') }}</div>
                            <div class="report-title-secondary">{{ getString('pos') }}</div>
                        </div>
                        <div class="report-value">
                            <div class="report-value-main">{{ transactionsReportData ? readableEurValue(transactionsReportData.fee/100) : '-' }}</div>
                            <div class="report-value-secondary">{{ transactionsReportData ? readableEurValue(transactionsReportData.order_fee/100) : '-' }}</div>
                            <div class="report-value-secondary">{{ transactionsReportData ? readableEurValue(transactionsReportData.pos_fee/100) : '-' }}</div>
                        </div>
                    </div>
                    <div class="report-row">
                        <div class="report-title">
                            <div class="report-title-main">{{ getString('revenue') }}</div>
                            <div class="report-title-secondary">{{ getString('orders') }}</div>
                            <div class="report-title-secondary">{{ getString('pos') }}</div>
                        </div>
                        <div class="report-value">
                            <div class="report-value-main">{{ transactionsReportData ? readableEurValue(transactionsReportData.amount/100) : '-' }}</div>
                            <div class="report-value-secondary">{{ transactionsReportData ? readableEurValue(transactionsReportData.order_amount/100) : '-' }}</div>
                            <div class="report-value-secondary">{{ transactionsReportData ? readableEurValue(transactionsReportData.pos_amount/100) : '-' }}</div>
                        </div>
                    </div>
                    <div class="title-divider mt-10">{{getString('details')}}</div>
                    <div class="report-row">
                        <div class="report-title">
                            <div class="report-title-main">{{ getString('total_costs') }}</div>
                            <div class="report-title-secondary">{{ getString('orders') }}</div>
                            <div class="report-title-secondary">{{ getString('pos') }}</div>
                        </div>
                        <div class="report-value">
                            <div class="report-value-main">{{ transactionsReportData ? readableEurValue(transactionsReportData.network_costs+transactionsReportData.stripe_costs) : '-' }}</div>
                            <div class="report-value-secondary">{{ transactionsReportData ? readableEurValue(transactionsReportData.order_network_costs+transactionsReportData.order_stripe_costs) : '-' }}</div>
                            <div class="report-value-secondary">{{ transactionsReportData ? readableEurValue(transactionsReportData.pos_network_costs+transactionsReportData.pos_stripe_costs) : '-' }}</div>
                        </div>
                    </div>
                    <div class="report-row">
                        <div class="report-title">
                            <div class="report-title-main">{{ getString('stripe_costs') }}</div>
                            <div class="report-title-secondary">{{ getString('orders') }}</div>
                            <div class="report-title-secondary">{{ getString('pos') }}</div>
                        </div>
                        <div class="report-value">
                            <div class="report-value-main">{{ transactionsReportData ? readableEurValue(transactionsReportData.stripe_costs) : '-' }}</div>
                            <div class="report-value-secondary">{{ transactionsReportData ? readableEurValue(transactionsReportData.order_stripe_costs) : '-' }}</div>
                            <div class="report-value-secondary">{{ transactionsReportData ? readableEurValue(transactionsReportData.pos_stripe_costs) : '-' }}</div>
                        </div>
                    </div>
                    <div class="report-row">
                        <div class="report-title">
                            <div class="report-title-main">{{ getString('network_costs') }}</div>
                            <div class="report-title-secondary">{{ getString('orders') }}</div>
                            <div class="report-title-secondary">{{ getString('pos') }}</div>
                        </div>
                        <div class="report-value">
                            <div class="report-value-main">{{ transactionsReportData ? readableEurValue(transactionsReportData.network_costs) : '-' }}</div>
                            <div class="report-value-secondary">{{ transactionsReportData ? readableEurValue(transactionsReportData.order_network_costs) : '-' }}</div>
                            <div class="report-value-secondary">{{ transactionsReportData ? readableEurValue(transactionsReportData.pos_network_costs) : '-' }}</div>
                        </div>
                    </div>
                    <div class="report-row">
                        <div class="report-title">
                            <div class="report-title-main">{{ getString('transactions') }}</div>
                            <div class="report-title-secondary">{{ getString('orders') }}</div>
                            <div class="report-title-secondary">{{ getString('pos') }}</div>
                        </div>
                        <div class="report-value">
                            <div class="report-value-main">{{ transactionsReportData ? transactionsReportData.transactions : '-' }}</div>
                            <div class="report-value-secondary">{{ transactionsReportData ? transactionsReportData.order_transactions : '-' }}</div>
                            <div class="report-value-secondary">{{ transactionsReportData ? transactionsReportData.pos_transactions : '-' }}</div>
                        </div>
                    </div>
                </div>
            </div>
		</AppBottomSheet>
		<AppBottomSheet :forceWidth="bottomSheetWidth" :active="quotesHistoryActive" @toggling-popup="v => quotesHistoryActive = v">
			<AppTable 
				v-if="quotesHistoryActive"
				:fetch-function="fetchQuotes"
				:table-headings="quotesTableHeadings"
				:title="getString('quotes_history')"
				:items="quotes"
				:total-items="quotesTotalItems">
				<template #actions="{ item }">
					<AppTableButtonGroup :buttons="getQuoteActions(item)" :item="item" 
						@picked-edit="editQuote(item)" 
						@picked-mark-paid="markQuoteAsPaid(item)" 
						@picked-mark-canceled="markQuoteAsCanceled(item)" />
				</template>
			</AppTable>
		</AppBottomSheet>
		<AppPopup
			:title="getString('confirm_change_payouts')"
			:body-text="getString('confirm_change_payouts_explain')"
			:buttons="popupChangePayoutsButtons"
			ref="confirm-payout"
		/>
	</div>
</template>

<script>
import AppButton from "@/shared/components/AppButton.vue";
import AppPopup from "@/shared/components/AppPopup.vue";
import AppNotification from "@/shared/components/AppNotification.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import AppSelect from "@/shared/components/AppSelect.vue";
import AppNumberInput from "@/shared/components/AppNumberInput.vue";
import {useSuperStore, useUserStore} from "@/user/stores";
import {useLanguageStore, useSvgStore, useCurrencyStore} from "@/shared/stores";
import {formatDateTime, getLocalDateFromServer, formatDateTimeWithSeconds} from "@/shared/helpers/dates";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import AppList from "@/shared/components/AppList.vue";
import FeeRulesAppList from "@/user/components/super/FeeRulesAppList.vue";
import AppClickableIcon from "@/shared/components/AppClickableIcon.vue";
import NewInvoice from "@/user/components/super/NewInvoice.vue";
import AppSwitch from "@/shared/components/AppSwitch.vue";
import AppTable from "@/shared/components/AppTable.vue";
import AppTableButtonGroup from "@/shared/components/AppTableButtonGroup.vue";
import API from "@/shared/helpers/API";

export default {
	components: {
		NewInvoice,
		AppClickableIcon,
		FeeRulesAppList, AppList, ModalHeader, AppBottomSheet, AppPopup, AppNumberInput, AppTextInput, AppSelect, AppNotification, AppButton, AppSwitch, AppTable, AppTableButtonGroup
	},
	setup() {
		const userStore = useUserStore();
		const languageStore = useLanguageStore();
		const superStore = useSuperStore();
		const svgStore = useSvgStore();
		const currencyStore = useCurrencyStore();
		return {userStore, currencyStore, formatDateTime,  superStore, svgStore, readableEurValue: languageStore.readableEurValue, languageStore, getString: languageStore.getString}
	},
	name: "BusinessSuperDropdown",
	props: {
		business: {type: Object, required: true},
	},
	mixins: [WindowWidth],
	data() {
		return {
			linkToShare: '',
			loadingLink: false,
			loadingType: false,
			loadingCommOp: false,
			loadingCommTerm: false,
			loadingCredit: false,
			loadingReport: false,
			loadingTransferPrefix: false,
			loadingPayoutInterval: false,
			loadingPayoutDelay: false,
			transactionsReportData: null,
			currentBusinessIdReport: null,
			newInvoiceActive: false,
			quotesHistoryActive: false,
			quotes: [],
			quotesTotalItems: 0,
			editingQuote: null,
			selectedRange: {
				start: this.getStartDate(),
				end: this.getEndDate()
			},
			DELAY_VALUES: [1,2,3,7],
			INTERVAL_VALUES: ['daily','weekly','monthly','manual'],
			savingPayouts: null,
			delaySelectItems: [this.getString('one_day'), this.getString('x_days', ['2']), this.getString('x_days', ['3']), this.getString('x_days', ['7'])],
			intervalSelectItems: [this.getString('daily'), this.getString('weekly'), this.getString('monthly'), this.getString('manual_payout_interval')],
			popupChangePayoutsButtons: [
				{text: this.getString('cancel'), callback: null, negative: true},
				{text: this.getString('ok'), callback: this.confirmPayout},
			],
			savingTapToPayFlag: false
		}
	},
	computed: {
		disabledQuote() {
			let currBusinessCurrency = this.currencyStore.countryCurrency(this.business?.country);
			return currBusinessCurrency?.code !== 'EUR';
		},
		countryMismatchPhrase() {
			return this.getString('business_country_mismatch', [COUNTRY, this.business?.country]);
		},
		correctCountry() {
			return this.business?.country === COUNTRY;
		},
		countryHost() {
			if(this.correctCountry) {
				return null
			}
			if(!this.business?.country || !this.business?.country?.toUpperCase || typeof COUNTRIES === 'undefined') { return null; }
			let finalCountry = null;
			for (let countryKey in COUNTRIES) {
				if(this.business.country.toUpperCase() === COUNTRIES[countryKey].country) {
					finalCountry = COUNTRIES[countryKey];
					break;
				}
			}
			if(finalCountry && COUNTRY !== finalCountry.country) {
				return finalCountry.host;
			}
			return null;
		},
		countryAdminUrl() {
			if(!this.countryHost) {
				return null
			}
			let protocol = (location?.protocol || "https:") + "//";
			return protocol + this.countryHost+"/user/super";
		},
		bottomSheetWidth() {
			if (this.windowWidth < 550) {
				return "100%";
			} else if (this.windowWidth < 768) {
				return "550px";
			} else if (this.windowWidth < 1080) {
				return "720px";
			} else {
				return "960px";
			}
		},
		bottomSheetWidthSmall() {
			if (this.windowWidth < 800) {
				return "100%";
			} else {
				return "550px";
			}
		},
		periodPhrase() {
			let endDateDB = new Date(this.selectedRange.end);
			let startDateDB = new Date(this.selectedRange.start);
			return this.localeDateString(startDateDB, true) + ' - ' + this.localeDateString(endDateDB, true);
		},
		feerulesStandard() {
			return this.formatFeeRulesToAppList('standard');
		},
		feerulesPOS() {
			return this.formatFeeRulesToAppList('pos');
		},
		editablePOSFees() {
			return this.business?.active_plan !== null;
		},
		planPOSDefaultFees() {
			switch (this.business?.active_plan) {
				case 'basic': return 1.6;
				case 'pro': return 1.4;
				case 'allinone': return 1.2;
				case 'enterprise': return 1.2;
				default: return 2.0;
			}
		},
		diabledEditPayouts() {
			return !this.business?.stripe_account;
		},
		diabledEditPayoutsDelay() {
			return this.diabledEditPayouts || this.business?.payout_interval === 'manual' || this.currentStripeIntervalIndex() === 3;
		},
		quotesTableHeadings() {
			return [
				{text: this.getString('quote_id'), cols: 1, fieldName: 'quote_id'},
				{text: this.getString('date'), cols: 2, fieldName: 'date'},
				{text: this.getString('amount'), cols: 2, fieldName: 'total_quote'},
				{text: this.getString('status'), cols: 2, fieldName: 'status'},
				{text: this.getString('payment_link'), cols: 3, fieldName: 'payment_link', slotName: 'payment_link'},
				{text: this.getString('actions'), cols: 2, fieldName: 'actions', slotName: 'actions'}
			];
		},
	},
	mounted() {
		this.checkQuotesAvailability();
	},
	methods: {
		getEffectivePOSPercent(percent) {
			// If the custom percent is greater than the plan default,
			// the system will apply the plan default instead
			return Math.min(percent, this.planPOSDefaultFees);
		},
		checkQuotesAvailability() {
			// Fetch quotes count to show/hide the quotes history button
			API.init().getSuperQuotes(this.business?.business_id || 0, {limit: 1, offset: 0}, (response) => {
				if (response.result === 'OK') {
					this.quotesTotalItems = response.total_items || 0;
				}
			});
		},
		currentStripeDelayIndex() {
			let currentIndex = 0;
			let select = this.$refs['business-payout-delay'];
			if(select && select.currentIndex) {
				currentIndex = select.currentIndex;

			}
			return currentIndex;
		},
		currentStripeIntervalIndex() {
			let currentIndex = 0;
			let select = this.$refs['business-payout-interval'];
			if(select && select.currentIndex) {
				currentIndex = select.currentIndex;
			}
			return currentIndex;
		},
		clickSavePayoutInterval() {
			this.savingPayouts = 'interval';
			this.$refs['confirm-payout'].toggleStatus(true);
		},
		clickSavePayoutDelay() {
			this.savingPayouts = 'delay';
			this.$refs['confirm-payout'].toggleStatus(true);
		},
		confirmPayout() {
			if(this.savingPayouts === 'interval') {
				this.savePayoutInterval();
			} else if(this.savingPayouts === 'delay') {
				this.savePayoutDelay();
			}
			this.savingPayouts = null;
		},
		savePayoutInterval() {
			if(this.loadingPayoutInterval || this.loadingPayoutDelay) { return }
			this.loadingPayoutInterval = true;
			this.$refs['confirm-payout'].toggleStatus(false);
			this.superStore.savePayoutInterval(this.business.business_id, this.INTERVAL_VALUES[this.currentStripeIntervalIndex()], (success) => {
				this.loadingPayoutInterval = false;
				if(success) {
					this.business.payout_interval = this.INTERVAL_VALUES[this.currentStripeIntervalIndex()];
					this.$refs['success-generic'].openNotification();
				} else {
					this.$refs['error-generic'].openNotification();
				}
			});
		},
		savePayoutDelay() {
			if(this.loadingPayoutInterval || this.loadingPayoutDelay) { return }
			this.loadingPayoutDelay = true;
			this.$refs['confirm-payout'].toggleStatus(false);
			this.superStore.savePayoutDelay(this.business.business_id, this.DELAY_VALUES[this.currentStripeDelayIndex()], (success) => {
				this.loadingPayoutDelay = false;
				if(success) {
					this.business.payout_delay = this.DELAY_VALUES[this.currentStripeDelayIndex()];
					this.$refs['success-generic'].openNotification();
				} else {
					this.$refs['error-generic'].openNotification();
				}
			});
		},
		changePrefix(value) {
			if(value.length === 0) {
				this.business.transfer_prefix = null;
				return;
			}
			let valueSan = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
			if(valueSan.length >= 20) {
				valueSan = valueSan.substring(0, 20);
			}
			this.business.transfer_prefix = valueSan;
		},
		savePrefix() {
			this.loadingTransferPrefix = true;
			this.superStore.changeTransferPrefix(this.business.business_id, this.business.transfer_prefix, (r) => {
				this.loadingTransferPrefix = false;
				if(r) {
					this.$refs['success-generic'].openNotification();
				} else {
					this.$refs['error-generic'].openNotification();
				}
			});
		},
		formatFeeRulesToAppList( type ) {
			let appListRules = [];
			if ( this.business && this.business.feerules && this.business.feerules[type] ) {
				this.business.feerules[type].forEach(rule => {
					let minTitle = this.readableEurValue(rule.min_limit/100);
					rule.temp_id = Math.random().toString(36).slice(2);
					let newRule = {
						title: this.getString('from') + " " + minTitle + " " + (rule.max_limit ? this.getString('to') + " " + this.readableEurValue(rule.max_limit/100) : this.getString('onwards')),
						id: rule.fee_rule_id,
						tempId: rule.temp_id,
						type: rule.fee_type,
						min: rule.min_limit,
						max: rule.max_limit,
						fixed: rule.fixed,
						percent: rule.percent,
						premium: rule.premium_percent,
						customSlot: "feerule",
						dropdownSlot: "feeRulesMod",
						active: rule.active,
						toActive: rule.max_limit ?? false,
					};
					if ( type === "standard" ) {
						newRule.alternative_fixed = rule.alternative_fixed;
						newRule.alternative_percent = rule.alternative_percent;
					}
					appListRules.push( newRule );
				});
			}
			appListRules.sort((a,b) => {
				return a.min === b.min ? a.max - b.max : a.min - b.min;
			});
			return appListRules;
		},
		getStartDate() {
			let date = new Date();
			date.setMonth(date.getMonth() - 1);
			date.setHours(3,0,0);
			return date;
		},
		getEndDate() {
			let date = new Date();
			date.setDate(date.getDate() - 3);
			date.setHours(3,0,0);
			return date;
		},
		localeDateString(date, fullYear = false) {
			let options = {month: 'long', day: 'numeric'};
			if(fullYear) {
				options.year = 'numeric';
				options.month = 'short';
			}
			return date.toLocaleDateString([this.languageStore.lang || 'en-US'], options);
		},
		closeBottomSheet(isOpen) {
			if(!isOpen) {
				this.transactionsReportData = null;
			}
		},
		closeBottomSheetInvoice(isOpen) {
			if(!isOpen) {
				this.newInvoiceActive = false;
				this.editingQuote = null;
			}
		},
		selectedStripeIntervalIndex(interval) {
			return this.INTERVAL_VALUES.indexOf(interval);
		},
		selectedStripeDelayIndex(delay) {
			return this.DELAY_VALUES.indexOf(delay);
		},
		getTypeIndex(type) {
			switch(type) {
				case 'restaurant': return 0;
				case 'events': return 1;
				case 'other': return 2;
				default: return 0;
			}
		},
		changeBusinessType(business) {
			let type = this.$refs['business-type'].currentIndex;
			let typeValue = null;
			switch(type) {
				case 0: typeValue = "restaurant"; break;
				case 1: typeValue = "events"; break;
				case 2: typeValue = "other"; break;
				default: typeValue = "restaurant"; break;
			}
			if(!typeValue) {
				this.$refs['error-generic'].openNotification();
				return;
			}
			this.loadingType = true;
			this.superStore.changeBusinessType(business.business_id, typeValue, (r) => {
				this.loadingType = false;
				if(r) {
					this.$refs['success-generic'].openNotification();
				} else {
					this.$refs['error-generic'].openNotification();
				}
			});
		},
		changeBusinessCredit(business) {
			let credit = this.$refs['credit-debit'].currentValue*100;
			if(!credit) {
				credit = 0;
			}

			let repaymentRate = this.$refs['repayment-rate'].currentValue;
			repaymentRate = Math.min(100, Math.max(5, repaymentRate));
			this.loadingCredit = true;
			this.superStore.changeBusinessCredits(business.business_id, credit, repaymentRate, (r) => {
				this.loadingCredit = false;
				if(r === "success") {
					this.$refs['success-generic'].openNotification();
					this.business.openBalance = credit;
				} else if(r === "ultra") {
					this.$refs["ultra-not-comm"].openNotification();
				} else {
					this.$refs['error-generic'].openNotification();
				}
			});
		},
		getPermissionIcons(permissions) {
			let icons = [];

			['orders_creation', 'orders_edit', 'orders_pay', 'orders_discounts', 'orders_daily_closing'].forEach(orderPerm => {
				if(permissions.includes(orderPerm)) {
					icons.push({
						icon: this.svgStore.get('iconOperations'),
						title: orderPerm
					});
				}
			})
			if(permissions.includes('analytics')) {
				icons.push({
					icon: this.svgStore.get('iconAnalytics'),
					title: 'analytics'
				});
			}
			if(permissions.includes('menu')) {
				icons.push({
					icon: this.svgStore.get('iconMenu'),
					title: 'menu'
				});
			}
			if(permissions.includes('admin')) {
				icons.push({
					icon: this.svgStore.get('iconSettings'),
					title: 'admin'
				});
			}

			return icons;
		},
		forceLogin(user, country) {
			this.superStore.forceLogin(user.user_id, user.business_id, (r, token) => {
				if(r === "success") {
					// Determine the correct domain for this business's country
					let targetHost = null;
					if(country && country !== COUNTRY && typeof COUNTRIES !== 'undefined') {
						for (let countryKey in COUNTRIES) {
							if(country.toUpperCase() === COUNTRIES[countryKey].country) {
								targetHost = COUNTRIES[countryKey].host;
								break;
							}
						}
					}

					// Redirect to the appropriate domain
					if(targetHost && token) {
						// Cross-domain redirect: use params.php to pass token
						// Clear current domain's token since we're leaving
						sessionStorage.removeItem("oauth_token");
						let protocol = (location?.protocol || "https:") + "//";
						let targetUrl = protocol + targetHost + "/console/new/";
						// Security: Pass token in URL fragment (#) instead of query string
						// Fragments are not sent to server, preventing token exposure in logs
						window.location.replace(
							protocol +
							targetHost +
							"/console/new/params.php?url=" +
							encodeURIComponent(targetUrl) +
							"#login_token=" + encodeURIComponent(token)
						);
					} else {
						// Same domain: token already stored in sessionStorage
						window.location.href = URL_HTML+"/console/new/";
					}
				} else if(r === "ultra") {
					this.$refs["ultra-not"].openNotification();
				} else {
					this.$refs["error-generic"].openNotification();
				}
			});
		},
		saveNewFeeRules( type ) {
			if ( this.validateNewFeeRules( type ) ) {
				this.loadingCommTerm = true;
				this.superStore.changeFeeRules(this.business.business_id, type, JSON.stringify(this.business.feerules[type]), (r) => {
					this.loadingCommTerm = false;
					if(r === "success") {
						this.$refs["success-fee-rules"].openNotification();
					} else if(r === "lower_fee_percentage_only_ultra") {
						this.$refs["percentage-fee-low"].openNotification();
					} else if(r === "ultra") {
						this.$refs["ultra-not"].openNotification();
					} else if(r === "fees_not_editable_plan") {
						this.$refs["percentage-fee-low"].openNotification();
					} else {
						this.$refs["error-fee-rules"].openNotification();
					}
				});
			} else {
                this.$refs['error-validate-fee-rules'].openNotification();
            }
		},
		validateNewFeeRules( type ) {
			if(type === 'pos' && !this.editablePOSFees) {
				return false;
			}
			let newRules = this.business.feerules[type];
			let toCoverMin = [0];
			let toCoverMax = [null];
			for ( let i = 0; i < newRules.length; i++ ) {
				if ( newRules[i].max_limit !== null && newRules[i].max_limit < newRules[i].min_limit ) {
					this.$refs['error-validate-fee-rules'].openNotification();
					return false;
				}
				if ( toCoverMin.includes(newRules[i].min_limit) ) {
					toCoverMin.splice(toCoverMin.indexOf(newRules[i].min_limit), 1);
				} else {
					toCoverMax.push(newRules[i].min_limit-1);
				}
				if ( toCoverMax.includes(newRules[i].max_limit) ) {
					toCoverMax.splice(toCoverMax.indexOf(newRules[i].max_limit), 1);
				} else {
					if ( newRules[i].max_limit !== null ) {
						toCoverMin.push((newRules[i].max_limit+1));
					} else {
						this.$refs['error-validate-fee-rules'].openNotification();
						return false;
					}
				}
			}
			return (toCoverMax.length + toCoverMin.length) === 0;
		},
		addNewFeeRule( type ) {
			if ( this.business && this.business.feerules && this.business.feerules[type] ) {
				this.business.feerules[type].forEach(rule => {
					rule.active = false;
				});
			} else {
				this.business.feerules[type] = [];
			}
			let newRule = {
				title: this.getString('from') + " " + this.readableEurValue(0) + " " + this.getString('to') + " " + this.getString('onwards'),
				fee_rule_id: null,
				fee_type: type,
				min_limit: 0,
				max_limit: null,
				fixed: 0,
				percent: 0,
				premium_percent: 0,
				active: true,
			};
			if ( type === "standard" ) {
				newRule.alternative_fixed = 0;
				newRule.alternative_percent = 1;
			}
			this.business.feerules[type].push( newRule );
		},
		deleteFeeRule( item ) {
			this.business.feerules[item.type].forEach((rule,key) => {
				if ( rule.temp_id === item.tempId ) {
					this.business.feerules[item.type].splice(key,1);
				}
			});
		},
		changedValue( item, type, val, which ) {
			this.business.feerules[type].forEach(rule => {
				if (rule.temp_id === item.tempId) {
					if (which === "min") rule.min_limit = val;
					if (which === "max") {
						rule.max_limit = val;
						rule.toActive = !!val;
					}
					if (which === "fixed") rule.fixed = val;
					if (which === "percent") rule.percent = val;
					if (which === "premium") rule.premium_percent = val;
					if (which === "altfixed") rule.alternative_fixed = val;
					if (which === "altpercent") rule.alternative_percent = val;
				}
			});
		},
		toggleFeeRule( item, type ) {
			this.business.feerules[type].forEach(rule => {
				if (rule.temp_id === item.tempId) {
					rule.active = !rule.active;
				} else {
					rule.active = false;
				}
			});
		},
		reportCommissions(businessId) {
			this.currentBusinessIdReport = businessId;
			this.loadingReport = true;
			let start = this.selectedRange.start;
			start.setHours(3,0,0);
			let end = this.selectedRange.end;
			end.setHours(3,0,0);
			end.setDate(end.getDate()+1);
			this.superStore.reportCommissions(businessId, formatDateTimeWithSeconds(start), formatDateTimeWithSeconds(end), (r) => {
				this.loadingReport = false;
				if(r) {
					this.transactionsReportData = r;
				} else {
					this.$refs["error-generic"].openNotification();
				}
			});
		},
		changedCalendarDates() {
			if(this.currentBusinessIdReport) {
				this.reportCommissions(this.currentBusinessIdReport);
			}
		},
		saveTapToPayFlag() {
			this.savingTapToPayFlag = true;

			this.superStore.updateTapToPayFlag(this.business.business_id, this.business.allow_tap_to_pay, (r) => {
				this.savingTapToPayFlag = false;
				if(r) {
					this.$refs['success-generic'].openNotification();
				} else {
					this.$refs['error-generic'].openNotification();
				}
			});
		},
		fetchQuotes(filters, callback) {
			API.init().getSuperQuotes(this.business?.business_id || 0, filters, (response) => {
				if (response.result === 'OK') {
					this.quotes = (response.quotes || []).map((quote) => {
						return {
							...quote,
							date: getLocalDateFromServer(quote.date),
							total_quote: this.currencyStore.readableCurrencyValue(quote.total_quote),
						};
					});
					this.quotesTotalItems = response.total_items || 0;
					callback(true);
				} else {
					callback(false, response.details);
				}
			});
		},
		editQuote(quote, event) {
			// Previeni la propagazione dell'evento per evitare che si apra il link
			if (event) {
				event.stopPropagation();
			}
			
			API.init().getSuperQuoteDetails(quote.quote_id, (response) => {
				
				if (response && response.result === 'OK') {
					this.editingQuote = response.quote;
					this.newInvoiceActive = true;
					// Close the quotes history bottom sheet when opening the edit modal
					this.quotesHistoryActive = false;
				} else {
					// Mostro un messaggio di errore all'utente
					if (this.$refs['error-generic']) {
						this.$refs['error-generic'].openNotification();
					}
				}
			});
		},
		closeNewInvoice() {
			this.newInvoiceActive = false;
			this.editingQuote = null;
			// Refresh quotes count when closing the invoice modal
			this.checkQuotesAvailability();
		},
		getQuoteActions(item) {
			const actions = [];
			
			// Tasto Edit (senza outline, sempre visibile se non è paid o canceled)
			if (item.status !== 'paid' && item.status !== 'canceled') {
				actions.push({
					iconName: "icon-edit-blue",
					emitName: "picked-edit",
					title: this.getString('edit')
				});
			}
			
			// Tasto Pagato (solo per status 'created')
			if (item.status === 'created') {
				actions.push({
					iconName: "icon-wire-transfer",
					class: "outline",
					emitName: "picked-mark-paid", 
					title: this.getString('mark_paid')
				});
			}
			
			// Tasto Cancella (solo per status 'created')
			if (item.status === 'created') {
				actions.push({
					iconName: "Icons-trash",
					class: "outline", 
					emitName: "picked-mark-canceled",
					title: this.getString('mark_canceled')
				});
			}
			
			return actions;
		},
		markQuoteAsPaid(quote) {
			if (confirm('Sei sicuro di voler contrassegnare questo preventivo come pagato? Verrà generata una fattura automaticamente.')) {
				API.init().markQuoteAsPaid(quote.quote_id, (response) => {
					if (response.result === 'OK') {
						// Update the quote status in the local array
						const quoteIndex = this.quotes.findIndex(q => q.quote_id === quote.quote_id);
						if (quoteIndex !== -1) {
							this.quotes[quoteIndex].status = 'paid';
						}
						this.$refs['success-generic'].openNotification();
					} else {
						this.$refs['error-generic'].openNotification();
					}
				});
			}
		},
		markQuoteAsCanceled(quote) {
			if (confirm('Sei sicuro di voler annullare questo preventivo?')) {
				API.init().markQuoteAsCanceled(quote.quote_id, (response) => {
					if (response.result === 'OK') {
						// Update the quote status in the local array
						const quoteIndex = this.quotes.findIndex(q => q.quote_id === quote.quote_id);
						if (quoteIndex !== -1) {
							this.quotes[quoteIndex].status = 'canceled';
						}
						this.$refs['success-generic'].openNotification();
					} else {
						this.$refs['error-generic'].openNotification();
					}
				});
			}
		},
	}
}
</script>

<style lang="scss" scoped>
.cards-container {
	display: flex;
	flex-direction: row;
	flex-wrap: wrap;
	justify-content: space-between;
	width: 100%;
	gap: 10px;
	background-color: var(--color-alpha-1);
	box-shadow: inset 0 1px 2px var(--color-alpha-4);
	border-radius: 13px;
	padding: 10px;

	.card {
		width: calc(50% - 5px);
		background-color: var(--color-bg-primary-1);
		border-radius: 13px;
		box-shadow: 0 2px 3px var(--color-box-shadow-2);
		padding: 0 10px;

		@media (max-width: 900px) {
			width: 100%;
		}

		.card-title {
			font-size: 16px;
			font-weight: 500;
			color: var(--color-primary);
			border-bottom: 1px solid var(--color-primary);
			padding-top: 10px;
			padding-bottom: 8px;
		}

		.card-body {

			.current-balance {
				font-size: 14px;
				font-weight: 500;

				span {
					font-weight: 700;
				}

				&.positive {
					color: #62D18D;
				}

				&.negative {
					color: #E24B74;
				}
			}

			.change-balance {
				display: flex;
				flex-direction: column;
				align-items: flex-end;
				justify-content: flex-start;
			}

			.select-of-row {
				width: 150px;

				@media (max-width: 400px) {
					width: 100%;
					margin-bottom: 10px;
				}
			}

			.link-head {
				font-size: 14px;
				color: var(--color-primary);
				font-weight: 500;
			}

			.link-body {
				font-size: 14px;
				font-style: italic;
			}

			.commissions-container {
				display: flex;
				flex-direction: column;
				padding: 10px 0;

				:deep(.dropdown-container .dropdown .dropdown-content) {
					background: var(--color-alpha-1);
					box-shadow: inset 0 1px 2px var(--color-alpha-4);
					border-radius: 13px;
				}

				.list-item {
					padding: 0 0 10px 0;
					margin-top: 10px;
				}

				.list-item-header {
					display: flex;
					align-items: center;
					justify-content: space-between;
					width: 100%;
					cursor: pointer;
					:first-child {
						flex-grow: 2;
					}
				}

				.add-fee-rule {
					color: var(--color-primary);
					background: var(--color-alpha-1);
					padding: 10px;
					border-top: 1px solid var(--color-alpha-3);
					display: flex;
					justify-content: start;
					align-items: center;
					height: 40px;
					:deep(.icon-plus) {
						margin-right: 10px;
						path {
							fill: var(--color-primary);
						}
					}
					&:hover {
						cursor: pointer;
						background: var(--color-alpha-2);
					}
				}

				.list-item-fee-recap {
					padding-right: 10px;
					font-size: 80%;
					color: var(--txt-2);
					span {
						font-size: 100%;
						color: var(--color-txt);
					}
				}

				.commissions-row {
					display:flex;
					flex-direction: row;
					align-items: center;
					justify-content: space-between;
					width: 100%;

					.commissions-title {
						@include truncateString();
					}

				}
			}

			.users-container {
				display: flex;
				flex-direction: column;
				padding: 10px 0;
				max-height: 250px;
				overflow-y: auto;
				@include hideScrollbar;

				.mismatch-country-phrase {
					color: var(--txt-2);
					font-size: 12px;
					margin-bottom: 6px;
				}

				.user {
					display: flex;
					flex-direction: row;
					align-items: center;
					justify-content: space-between;
					padding-top: 6px;
					padding-bottom: 10px;
					border-bottom: 1px solid rgba(108, 51, 203, 0.3);
					width: 100%;

					&:last-child {
						border-bottom: none;
						padding-bottom: 0;
					}

					&:first-child {
						padding-top: 0;
					}

					.user-info {
						display: flex;
						flex-direction: column;
						max-width: calc(100% - 120px);

						.name {
							font-size: 14px;
							@include truncateString();
						}

						.permissions {
							display: flex;
							flex-direction: row;
							gap: 6px;
							padding-top: 4px;

							.permission {
								width: 20px;
								height: 20px;
								padding: 5px;
								border-radius: 8px;
								background-color: rgba(108, 51, 203, 0.8);
								display: flex;
								justify-content: center;
								align-items: center;

								:deep(svg) {
									width: 100%;
									height: 100%;

									.fill {
										fill: white;
									}

									.stroke {
										stroke: white;
									}

								}
							}
						}
					}
				}

			}
		}

		.divider-line {
			width: 100%;
			border-bottom: 1px solid rgba(108, 51, 203, 0.3);
		}
	}
}
.report-container {
	display: flex;
	flex-direction: column;
	width: 100%;
	padding-top: 5px;

	.date-range {
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: center;
		width: 100%;

		.period-phrases {
			padding: 8px 12px;
			border: 1px solid var(--color-primary);
			border-radius: 13px;
			cursor: pointer;

			p {
				color: var(--color-primary);
			}
		}

	}


	.title-divider {
		font-size: 14px;
		font-weight: 700;
		text-align: left;
	}

	.report-row {
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: space-between;
		width: 100%;
		padding: 10px 0;
		border-bottom: 1px solid rgba(108, 51, 203, 0.3);

		&:last-child {
			border-bottom: none;
		}

		&.no-border {
			border-bottom: none;
		}

		.report-title {
			@include truncateString();

			&-main {
				font-size: 14px;
				font-weight: 500;
				color: var(--color-primary);
			}

			&-secondary {
				font-size: 12px;
				font-weight: 400;
			}
		}

		.report-value {
			@include truncateString();

			&-main {
				font-size: 14px;
				font-weight: 500;
				color: var(--color-primary);
				text-align: right;
			}

			&-secondary {
				font-size: 12px;
				font-weight: 400;
				text-align: right;
			}

		}
	}
}
.quotes-buttons {
	padding: 10px 0;
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	flex-wrap: wrap;
	gap: 10px;
}
</style>